import { onMounted, onUnmounted, ref } from 'vue'
import LegalText from 'components/LegalText/LegalText.vue'
import NykBanner from 'components/NykBanner/NykBanner.vue'
import DitRaaderumCalculatorInputCard from './Components/DitRaaderumCalculatorInputCard/DitRaaderumCalculatorInputCard.vue'
import DitRaaderumCalculatorResultCard from './Components/DitRaaderumCalculatorResultCard/DitRaaderumCalculatorResultCard.vue'
import {
    createLocaleNumberFormatters,
    createLocaleNumberInputFormat,
    parseLocaleNumber,
} from 'foundation/js/utils/numberFormatting'

export default {
    name: 'DitRaaderumCalculator',
    components: {
        LegalText,
        NykBanner,
        DitRaaderumCalculatorInputCard,
        DitRaaderumCalculatorResultCard,
    },
    props: {
        apiUrl: {
            type: String,
            required: true,
        },
        locale: {
            type: String,
            default: 'da-DK',
        },
        labels: {
            type: Object,
            default: () => ({}),
        },
        legalLabels: {
            type: Object,
            default: () => ({}),
        },
    },
    setup(props) {
        const labels = props.labels
        const locale = props.locale || 'da-DK'
        const { formatNumber, formatCurrency } = createLocaleNumberFormatters(locale)
        const numberInputFormat = createLocaleNumberInputFormat(locale)
        const queryParams = new URLSearchParams(window.location.search)
        const getQueryValue = (key) => {
            const value = queryParams.get(key)
            return value !== null && /^\d+$/.test(value) && Number.isSafeInteger(Number(value)) ? value : ''
        }

        const houseValue = ref(getQueryValue('hv'))
        const remainingDebt = ref(getQueryValue('rd'))
        const result = ref(null)
        const errorMessage = ref('')
        const isErrorDismissed = ref(false)
        const isLoading = ref(false)
        let activeRequest = null

        const cancelPendingCalculation = () => {
            if (activeRequest) {
                activeRequest.abort()
                activeRequest = null
                isLoading.value = false
            }
        }

        const updateHouseValue = (value) => {
            if (houseValue.value !== value) {
                cancelPendingCalculation()
            }
            houseValue.value = value
        }

        const updateRemainingDebt = (value) => {
            if (remainingDebt.value !== value) {
                cancelPendingCalculation()
            }
            remainingDebt.value = value
        }

        const updateCalculationUrl = (houseValueAmount, remainingDebtAmount) => {
            const url = new URL(window.location.href)
            url.searchParams.set('hv', houseValueAmount)
            url.searchParams.set('rd', remainingDebtAmount)
            window.history.replaceState(window.history.state, '', url.toString())
        }

        const calculate = async () => {
            cancelPendingCalculation()
            errorMessage.value = ''
            isErrorDismissed.value = false
            result.value = null

            if (houseValue.value === '' || remainingDebt.value === '') {
                return
            }

            const houseValueAmount = parseLocaleNumber(houseValue.value, locale)
            const remainingDebtAmount = parseLocaleNumber(remainingDebt.value, locale)
            updateCalculationUrl(houseValueAmount, remainingDebtAmount)
            const controller = new AbortController()
            activeRequest = controller
            isLoading.value = true
            let editorDetail = ''

            try {
                const response = await fetch(props.apiUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    signal: controller.signal,
                    body: JSON.stringify({
                        houseValue: houseValueAmount,
                        remainingDebt: remainingDebtAmount,
                    }),
                })

                if (!response.ok) {
                    const error = await response.json()
                    editorDetail = typeof error.detail === 'string' ? error.detail : ''
                    throw new Error('Calculation failed')
                }

                const calculationResult = await response.json()
                if (activeRequest === controller) {
                    result.value = calculationResult
                }
            } catch {
                if (activeRequest === controller) {
                    errorMessage.value = editorDetail || labels.calculationError || 'Beregningen kunne ikke hentes. Prøv igen senere.'
                }
            } finally {
                if (activeRequest === controller) {
                    activeRequest = null
                    isLoading.value = false
                }
            }
        }

        onMounted(() => {
            if (houseValue.value !== '' && remainingDebt.value !== '') {
                calculate()
            }
        })
        onUnmounted(cancelPendingCalculation)

        const dismissError = () => {
            isErrorDismissed.value = true
        }

        return {
            numberInputFormat,
            houseValue,
            remainingDebt,
            result,
            errorMessage,
            isErrorDismissed,
            isLoading,
            updateHouseValue,
            updateRemainingDebt,
            calculate,
            dismissError,
            formatNumber,
            formatAmount: formatCurrency,
            initialHouseValue: houseValue.value,
            initialRemainingDebt: remainingDebt.value,
        }
    },
}